<template>
  <span
    @mouseenter="!history ? showDate = true : showDate = false"
    @mouseleave="showDate = false"
    @click="showDate = false"
  >
    <date-range-picker
      ref="picker"
      v-model="dateRange"
      :opens="opens"
      :min-date="minDate"
      :max-date="maxDate"
      :linked-calendars="linkedCalendars"
      :always-show-calendars="true"
      :locale-data="{format: `d mmm yyyy (HH:MM)`}"
      time-picker
      :time-picker24-hour="false"
      close-on-esc
      @select="clearRangeSelected"
      @update="updateValues"
      @toggle="togglePicker"
    >
      <div
        v-if="resolved"
        id="date-picker-input"
        slot="input"
      >
        <v-icon
          size="16"
          color="white"
          class="mr-2"
        >
          $faClock
        </v-icon>
        <span style="flex:1;">
          {{ text }}
        </span>
        <v-icon
          class="ml-2"
          size="16"
          color="white"
        >
          {{ pickerOpen ? '$faChevronUp' : '$faChevronDown' }}
        </v-icon>
      </div>
      <div
        slot="ranges"
        class="ranges"
      >
        <ul>
          <li
            v-for="range in ranges"
            :key="range.label"
            :class="{active: range.label === rangeSelected.label}"
            @click="updateSelectedRange(range)"
          >
            {{ range.label }}
          </li>
        </ul>
      </div>
    </date-range-picker>
    <v-fade-transition>
      <div
        v-if="showDate && !pickerOpen"
        class="date-picker-hover d-flex justify-space-around pa-2"
      >
        <div class="d-flex flex-column">
          <span>{{ hoverText.startDate.toUpperCase() }}</span>
          <span>{{ hoverText.startTime }}</span>
        </div>
        <v-icon
          color="white"
          size="16px"
        >
          $faArrowRight
        </v-icon>
        <div class="d-flex flex-column">
          <span>{{ hoverText.endDate.toUpperCase() }}</span>
          <span>{{ hoverText.endTime }}</span>
        </div>
      </div>
    </v-fade-transition>
  </span>
</template>

<script>
  import DateRangePicker from 'vue2-daterange-picker'
  import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

  const seconds = 1000
  const minutes = 60 * seconds
  const hours = 60 * minutes
  const days = 24 * hours

  export default {
    name: 'DatePicker',
    components: {
      DateRangePicker,
    },
    props: {
      text: String,
      hoverText: Object,
      history: {
        type: Boolean,
        default: false,
      },
      raised: Boolean,
    },
    data: () => ({
      showDate: false,
      clock: null,
      now: Date.now(),
      filterData: [],
      barText: '',
      resolved: false,
      opens: 'right',
      minDate: '2020-01-01 00:00:00',
      dateRange: {
        startDate: new Date(Date.now() - 30 * minutes).toISOString(),
        endDate: new Date().toISOString(),
      },
      rangeSelected: {},
      singleDatePicker: false,
      showDropdowns: true,
      autoApply: false,
      linkedCalendars: false,
      alwaysShowCalendars: true,
      pickerOpen: false,
    }),
    computed: {
      deltas () {
        return [
          { label: 'Last 30 minutes', delta: 30 * minutes },
          { label: 'Last 1 hour', delta: hours },
          { label: 'Last 3 hours', delta: 3 * hours },
          { label: 'Last 24 hours', delta: 24 * hours },
          { label: 'Last 3 days', delta: 3 * days },
          { label: 'Last 7 days', delta: 7 * days },
        ]
      },
      ranges () {
        return this.deltas.map(({ label, delta }) => ({ label, delta, value: [new Date(this.now - delta), new Date(this.now)] }))
      },
      maxDate () {
        return new Date(this.now).toISOString()
      },
    },
    mounted () {
      this.resolved = true
      this.clock = this.updateNow()
    },
    destroyed () {
      clearTimeout(this.clock)
    },
    methods: {
      clearRangeSelected () {
        this.rangeSelected = {}
      },
      updateSelectedRange (range) {
        this.rangeSelected = range
        this.dateRange.startDate = range.value[0]
        this.dateRange.endDate = range.value[1]
      },
      updateValues (dates) {
        const start = new Date(dates.startDate).getTime()
        const end = new Date(dates.endDate).getTime()
        const diff = (end - start)
        const payload = { dates, diff }
        this.$emit('update-date', payload, this.rangeSelected)
      },
      updateNow () {
        return setTimeout(() => {
          this.now = Date.now()
          clearTimeout(this.clock)
          this.clock = this.updateNow()
        }, 30 * seconds)
      },
      togglePicker (state) {
        this.pickerOpen = state
      },
    },
  }
</script>
<style lang="scss">
  .vue-daterange-picker {
    min-width: 250px;
    .form-control.reportrange-text {
      border: none;
      padding: 0;
      border-radius: 5px;
      background-color:$primary;
      #date-picker-input {
        color:white;
        display: flex;
        align-items: center;
        width: 248px;
        height: 40px;
        padding: 0 8px;
      }
    }
  }
  .date-picker-hover {
    color:white;
    background-color:$primary;
    width:310px;
    height:60px;
    position:absolute;
    top:64px;
    left:20px;
    z-index:200;
    border-radius: 5px;
    align-items: center;
  }
  .daterangepicker {
    top: 100%;
    left: 0 !important;
    color: #535353;
    font-weight: 500;
    font-family: "Lato", sans-serif !important;
    td {
      padding: 4px;
    }
    td.active.in-range {
      border-radius: 50%;
      background-color: $success !important;
    }
    .row {
      flex-wrap: nowrap;
    }
    .ranges {
      margin-top:0 !important;
      background-color: $muted;
      color: white;
      font-weight:500;
      width:169px;
      ul {
        width:169px;
        li {
          padding-left:20px;
          &.active {
            background-color: $nav-highlight;
          }
          &:hover {
            background-color:$nav-highlight;
            color:white;
          }
        }
      }
    }
    .calendars-container {
      td.disabled {
        background-color:$na-dark-cream;
      }
    }
    .calendar-time {
      display:flex;
      justify-content: center;
      .hourselect, .minuteselect, .ampmselect {
        border-radius: 5px;
        background-color: $na-off-white;
        margin: 0 .5rem;
        width: 65px;
        height: 44px;
        text-align-last:center;
        font-size:15px;
        font-weight:600;
      }
    }
    .drp-buttons {
      background-color:$na-dark-cream;
      color:$base;
      display:flex !important;
      align-items:center;
      justify-content: flex-end;
      height: 62px;
      .drp-selected {
        font-family: "Degular", sans-serif;
        margin-right:130px;
        font-size: 16px;
        font-weight: 600;
        display:flex;
      }
      .cancelBtn {
        font-size: 14px;
        text-transform: uppercase;
        font-weight: 400;
        height:40px;
        width:121px;
      }
      .applyBtn {
        color: white;
        background-color: $primary;
        font-size: 14px;
        text-transform: uppercase;
        font-weight: 400;
        border-radius: 5px;
        box-shadow: 0 3px 3px 1px rgba(0,0,0,.3);
        margin-right:9px;
        height:40px;
        width:121px;
      }
    }
  }
#date-range-container.raised {
box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2),
                0px 2px 2px 0px rgba(0, 0, 0, 0.14),
                0px 1px 5px 0px rgba(0, 0, 0, 0.12);
}
</style>
